import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";

const AuthCallback = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const processedRef = useRef(false);
  const maxRetries = 2;

  useEffect(() => {
    const processAuth = async () => {
      // Prevent double processing
      if (processedRef.current) {
        console.log("Auth already processed, skipping");
        return;
      }
      
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        console.log("Auth callback - hash:", hash?.substring(0, 50));
        
        const sessionId = hash.split('session_id=')[1]?.split('&')[0];

        if (!sessionId) {
          console.error("No session_id found in URL hash:", hash);
          setError("No session ID found. Please try again.");
          setTimeout(() => navigate("/", { replace: true }), 2000);
          return;
        }

        // Mark as processing immediately
        processedRef.current = true;
        setProcessing(true);

        console.log("Processing auth with session_id:", sessionId.substring(0, 15) + "...");
        console.log("API endpoint:", `${API}/auth/session`);

        // Exchange session_id for session_token with timeout
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { 
            withCredentials: true,
            timeout: 15000 // 15 second timeout
          }
        );

        console.log("Auth response status:", response.status);
        
        const { user } = response.data;
        
        if (!user) {
          throw new Error("No user data in response");
        }
        
        console.log("Auth successful for user:", user.email);
        
        // Update auth state FIRST
        login(user);

        // Clean URL by removing the hash with session_id
        window.history.replaceState({}, document.title, window.location.pathname);

        // Small delay to ensure cookie is set and state is updated
        await new Promise(resolve => setTimeout(resolve, 300));

        // Navigate based on profile completion
        if (user.profile_completed) {
          console.log("Navigating to dashboard");
          navigate("/dashboard", { replace: true });
        } else {
          console.log("Navigating to onboarding");
          navigate("/onboarding", { replace: true });
        }
      } catch (error) {
        console.error("Auth error details:", {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          code: error.code,
          url: `${API}/auth/session`
        });
        
        // Retry logic for network errors
        if (retryCount < maxRetries && (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' || !error.response)) {
          console.log(`Retrying auth (attempt ${retryCount + 2}/${maxRetries + 1})...`);
          processedRef.current = false;
          setRetryCount(prev => prev + 1);
          await new Promise(resolve => setTimeout(resolve, 1000));
          processAuth();
          return;
        }
        
        const errorMessage = error.response?.data?.detail || 
                            error.message || 
                            "Authentication failed. Please try again.";
        setError(errorMessage);
        processedRef.current = false;
        setProcessing(false);
        setTimeout(() => navigate("/", { replace: true }), 3000);
      }
    };

    processAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryCount]);

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center">
      <div className="text-center space-y-4">
        {error ? (
          <>
            <div className="text-red-500 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-red-600 font-medium">{error}</p>
            <p className="text-muted-foreground text-sm">Redirecting to home...</p>
          </>
        ) : (
          <>
            <div className="animate-pulse">
              <img 
                src="https://allwombs.com/_assets/media/922b5261d783455a5925488bfb8d8bf8.png" 
                alt="WOMBS" 
                className="h-16 mx-auto opacity-50"
              />
            </div>
            <p className="text-sage-dark font-medium">
              {retryCount > 0 ? `Retrying... (${retryCount}/${maxRetries})` : "Signing you in..."}
            </p>
            {processing && (
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-sage"></div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
